import type {
  ActivityCockpitSnapshot,
  CockpitCoverage,
  CockpitProvenance,
  CockpitSectionStatus,
  UsageReason,
} from '@internal/spassu-code-contract';
import type { CockpitMetricView } from './cockpitViewModel';
import {
  formatCanonicalCost,
  formatCanonicalInteger,
  formatDurationMs,
} from './formatters';
import { limitationLabel, sectionStatusLabel } from './semanticLabels';
import {
  metricReasonCause,
  metricReasonGuidance,
} from './metricReasonGuidance';

export function createCockpitMetricViews(
  snapshot: ActivityCockpitSnapshot,
): CockpitMetricView[] {
  const usage = snapshot.sections.usage.value;
  return [
    leadTimeMetric(snapshot),
    unclassifiedTimeMetric(snapshot),
    usageMetric(
      'input-tokens',
      'Tokens de entrada',
      usage?.inputTokens.value ?? null,
      usage?.inputTokens.coverage ?? 'none',
      snapshot,
    ),
    usageMetric(
      'output-tokens',
      'Tokens de saída',
      usage?.outputTokens.value ?? null,
      usage?.outputTokens.coverage ?? 'none',
      snapshot,
    ),
    ...costMetrics(snapshot),
  ];
}

function leadTimeMetric(snapshot: ActivityCockpitSnapshot): CockpitMetricView {
  const section = snapshot.sections.time;
  const rawValue = section.value?.leadTimeMs ?? null;
  return metric({
    id: 'lead-time',
    label: 'Tempo total observado',
    rawValue,
    displayValue: formatDurationMs(rawValue),
    // formatDurationMs already carries the unit; a second one only repeats it
    unit: null,
    status: section.status,
    coverage: section.coverage,
    provenance: section.provenance,
    explanation: 'Do primeiro evento elegível até a última observação.',
    missingReason: 'A janela ainda não possui início e fim elegíveis.',
    relation: 'Representa a janela que contém os demais tempos observados.',
    limitations: section.limitations,
    technicalValue: rawValue === null ? null : `${rawValue} ms`,
    primary: true,
    updatedAt: section.updatedAt,
    sourceCutoffAt: snapshot.sourceCutoffAt,
    ...timeContext(snapshot, false),
    proportion: timeProportion(snapshot, false),
    reasonCode: rawValue === null ? 'time-window-unavailable' : null,
  });
}

function unclassifiedTimeMetric(
  snapshot: ActivityCockpitSnapshot,
): CockpitMetricView {
  const section = snapshot.sections.time;
  const rawValue = section.value?.totalsMs.unclassified ?? null;
  return metric({
    id: 'unclassified-time',
    label: 'Tempo sem classificação',
    rawValue,
    displayValue: formatDurationMs(rawValue),
    // formatDurationMs already carries the unit; a second one only repeats it
    unit: null,
    status: section.status,
    coverage: section.coverage,
    provenance: section.provenance,
    explanation: 'Intervalos sem evidência suficiente para classificação.',
    missingReason: 'Não há projeção temporal disponível.',
    relation:
      'Faz parte do tempo total observado e não presume tempo de espera.',
    limitations: section.limitations,
    technicalValue: rawValue === null ? null : `${rawValue} ms`,
    primary: true,
    updatedAt: section.updatedAt,
    sourceCutoffAt: snapshot.sourceCutoffAt,
    ...timeContext(snapshot, true),
    proportion: timeProportion(snapshot, true),
    reasonCode: rawValue === null ? 'time-window-unavailable' : null,
  });
}

function costMetrics(snapshot: ActivityCockpitSnapshot): CockpitMetricView[] {
  const section = snapshot.sections.usage;
  return (section.value?.costs ?? []).map(cost =>
    metric({
      id: `cost-${cost.currency}`,
      label: `Custo em ${cost.currency}`,
      rawValue: cost.amount,
      displayValue: formatCanonicalCost(cost.amount, cost.currency),
      // the formatted value already shows the currency, and so does the label
      unit: null,
      status: section.status,
      coverage: section.coverage,
      provenance: section.provenance,
      nature: cost.nature,
      explanation: 'Custo reconciliado para a moeda informada.',
      missingReason: 'O custo não foi reconciliado.',
      relation: 'Custos de moedas diferentes permanecem separados.',
      technicalValue: `${cost.amount} ${cost.currency}`,
      primary: false,
      updatedAt: section.updatedAt,
      sourceCutoffAt: snapshot.sourceCutoffAt,
    }),
  );
}

function usageMetric(
  id: string,
  label: string,
  value: number | null,
  coverage: CockpitCoverage,
  snapshot: ActivityCockpitSnapshot,
): CockpitMetricView {
  const section = snapshot.sections.usage;
  const reason =
    id === 'input-tokens'
      ? section.value?.diagnostics?.inputReason ?? null
      : section.value?.diagnostics?.outputReason ?? null;
  return metric({
    id,
    label,
    rawValue: value,
    displayValue: formatCanonicalInteger(value),
    unit: 'tokens',
    status: section.status,
    coverage,
    provenance: section.provenance,
    explanation: 'Uso reconciliado a partir das evidências disponíveis.',
    missingReason: 'O uso não foi reconciliado para esta atividade.',
    relation: 'Entrada e saída são apresentadas separadamente.',
    limitations: section.limitations,
    technicalValue: value === null ? null : String(value),
    primary: true,
    updatedAt: section.updatedAt,
    sourceCutoffAt: snapshot.sourceCutoffAt,
    reasonCode: reason,
    counts: section.value?.diagnostics
      ? {
          observed: section.value.diagnostics.observedEvents,
          reconciled: section.value.diagnostics.reconciledUsages,
          pending: section.value.diagnostics.unreconciledEvents,
          excluded: section.value.diagnostics.excludedEvidence,
          exclusionReasons: section.value.diagnostics.exclusionReasons,
        }
      : null,
    usageObservationState: section.value?.diagnostics?.observationState ?? null,
    stale:
      snapshot.sessionObservability?.stale === true ||
      section.value?.diagnostics?.observationState === 'stale',
  });
}

function metric(input: MetricInput): CockpitMetricView {
  const status = metricStatus(input.status, input.coverage, input.rawValue);
  return {
    id: input.id,
    label: input.label,
    rawValue: input.rawValue,
    displayValue:
      input.rawValue === null ? missingValueLabel(status) : input.displayValue,
    unit: input.unit,
    status,
    coverage: input.coverage,
    provenance: input.provenance,
    nature: input.nature ?? null,
    explanation: input.explanation,
    unavailableReason: metricUnavailableReason(input),
    relation: input.relation,
    technicalValue: input.technicalValue,
    primary: input.primary,
    updatedAt: input.updatedAt,
    sourceCutoffAt: input.sourceCutoffAt,
    periodStartedAt: input.periodStartedAt ?? null,
    periodEndedAt: input.periodEndedAt ?? null,
    provisional: input.provisional ?? false,
    proportion: input.proportion ?? null,
    reasonCode:
      input.rawValue === null && input.status === 'failed'
        ? 'section-load-failed'
        : input.reasonCode ?? null,
    ...metricReasonGuidance(
      input.rawValue === null && input.status === 'failed'
        ? 'section-load-failed'
        : input.reasonCode ?? null,
    ),
    counts: input.counts ?? null,
    stale: input.stale ?? false,
    usageObservationState: input.usageObservationState ?? null,
  };
}

function timeProportion(
  snapshot: ActivityCockpitSnapshot,
  unclassified: boolean,
): string | null {
  const time = snapshot.sections.time.value;
  if (!time || time.leadTimeMs === null || time.leadTimeMs === 0) return null;
  const unclassifiedMs = Math.min(
    Math.max(time.totalsMs.unclassified, 0),
    time.leadTimeMs,
  );
  const ratio = unclassified
    ? unclassifiedMs / time.leadTimeMs
    : (time.leadTimeMs - unclassifiedMs) / time.leadTimeMs;
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(ratio);
  return unclassified
    ? `${formatted} do tempo observado permanece sem classificação.`
    : `${formatted} do tempo observado possui classificação.`;
}

function timeContext(snapshot: ActivityCockpitSnapshot, unclassified: boolean) {
  const intervals = (snapshot.sections.time.value?.intervals ?? []).filter(
    interval => !unclassified || interval.category === 'unclassified',
  );
  return {
    periodStartedAt: intervals[0]?.startedAt ?? null,
    periodEndedAt: intervals.at(-1)?.endedAt ?? null,
    provisional: snapshot.sections.time.value?.provisional ?? false,
  };
}

function metricUnavailableReason(input: MetricInput): string | null {
  if (input.rawValue !== null) return null;
  if (input.status === 'failed') {
    return 'A seção de métricas não pôde ser atualizada.';
  }
  const cause = metricReasonCause(input.reasonCode ?? null);
  if (cause) return cause;
  const limitation = input.limitations?.[0];
  return limitation ? limitationLabel(limitation) : input.missingReason;
}

function metricStatus(
  status: CockpitSectionStatus,
  coverage: CockpitCoverage,
  value: string | number | null,
): CockpitSectionStatus {
  if (value === null) {
    if (
      status === 'empty' ||
      status === 'failed' ||
      status === 'not-applicable'
    )
      return status;
    return 'unavailable';
  }
  if (status === 'partial' || coverage === 'partial') return 'partial';
  return status === 'empty' || status === 'unavailable' ? 'ready' : status;
}

function missingValueLabel(status: CockpitSectionStatus): string {
  return sectionStatusLabel(status);
}

interface MetricInput {
  id: string;
  label: string;
  rawValue: string | number | null;
  displayValue: string;
  unit: string | null;
  status: CockpitSectionStatus;
  coverage: CockpitCoverage;
  provenance: CockpitProvenance[];
  nature?: 'reported' | 'calculated';
  explanation: string;
  missingReason: string;
  relation: string | null;
  technicalValue: string | null;
  limitations?: string[];
  primary: boolean;
  updatedAt: string | null;
  sourceCutoffAt: string;
  periodStartedAt?: string | null;
  periodEndedAt?: string | null;
  provisional?: boolean;
  proportion?: string | null;
  reasonCode?:
    | UsageReason
    | 'time-window-unavailable'
    | 'section-load-failed'
    | null;
  counts?: CockpitMetricView['counts'];
  usageObservationState?: CockpitMetricView['usageObservationState'];
  stale?: boolean;
}
